import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-hot-toast'
import { sagaActions } from '@/redux/sagaActions'
import type { GroupLottoItemProps } from '@/ui-components/components/GroupLotto/GroupLotto.types'
import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '@/redux/reducers'
import type { NotificationMessage } from '@/ui-components/components/NotificationBanner/NotificationBanner.types'
import { useSelectionTabs } from '@/ui-components/pages/hooks/useSelectionTabs'
import useLottoCardForHomePage from '@/page/home/hooks/useLottoCardForHomePage'
import { trans } from '@/i18n/utils'
import { Language } from '@/i18n/constants'

const selectGroups = createSelector(
  (state: RootState) => state.games,
  (games) => ({
    groups: games.groups,
    loading: games.loading.groups,
    announcements: games.announcements,
    error: games.error,
  }),
)

const useHome = () => {
  const { i18n } = useTranslation()
  const createLottoCardPropsFromGame = useLottoCardForHomePage()

  const dispatch = useDispatch()

  const { groups, loading, error, announcements } = useSelector(selectGroups)

  const { selectedTabs, toggleTab, clearAll } = useSelectionTabs()

  useEffect(() => {
    dispatch({
      type: sagaActions.FETCH_GROUPS,
    })
    dispatch({
      type: sagaActions.FETCH_ANNOUNCEMENTS,
    })
    if (error) {
      toast.error(error)
      dispatch({ type: sagaActions.CLEAR_GAME_ERROR })
      dispatch({ type: sagaActions.CLEAR_GLOBAL_ERROR })
    }
  }, [dispatch, error])

  const messages: NotificationMessage[] =
    announcements?.map((i, index) => {
      const title = i18n.language === Language.TH ? i.title_th : i.title_en
      const content =
        i18n.language === Language.TH ? i.content_th : i.content_en
      return {
        id: index.toString(),
        text: `${title}: ${content}`,
      }
    }) ?? []

  // Create group and lotto items
  const groupsLotto: GroupLottoItemProps[] = groups
    .filter((group) => group.games.length > 0)
    .filter((group) => {
      if (selectedTabs.length === 0) return true
      return selectedTabs.includes(group.id.toString())
    })
    .map((group) => {
      const listLottoProps: GroupLottoItemProps = {
        id: group.id.toString(),
        name: trans(i18n, group.translations, 'name'),
        items: group.games
          .map((i) => {
            return createLottoCardPropsFromGame(i)
          })
          .sort((a, b) => {
            return Math.sign((b.isOpen ? 1 : 0) - (a.isOpen ? 1 : 0))
          }),
      }

      return listLottoProps
    })

  // Create tabs for each group
  const TABS = groups
    .filter((group) => group.games.length > 0)
    .map((group) => {
      return {
        label: trans(i18n, group.translations, 'name'),
        value: group.id.toString(),
      }
    })

  return {
    TABS,
    selectedTabs,
    toggleTab,
    messages,
    groupsLotto,
    loading,
    clearAll,
  }
}

export default useHome
